import {useEffect, useRef, useState} from 'react'
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native'
import {useSafeAreaInsets} from 'react-native-safe-area-context'
import * as Haptics from 'expo-haptics'
import {LinearGradient} from 'expo-linear-gradient'

import {type ThemePlayerProps} from '#/types/capsule'
import {getSettings, GRADIENT_PRESETS} from './settingsSchema'

export function PlayerComponent({
  capsule,
  currentPageIndex: _currentPageIndex,
  onPageComplete,
  onCapsuleComplete,
}: ThemePlayerProps) {
  const insets = useSafeAreaInsets()
  const settings = getSettings(capsule.behaviorSettings)
  const gradientColors = GRADIENT_PRESETS[settings.gradientPreset] as readonly [
    string,
    string,
    ...string[],
  ]

  const [answered, setAnswered] = useState(false)
  const [selected, setSelected] = useState<string[]>([])
  const [blankAnswers, setBlankAnswers] = useState<Record<string, string>>({})
  const [matchAnswers, setMatchAnswers] = useState<Record<string, string>>({})
  const [results, setResults] = useState<
    {
      pageId: string
      isCorrect: boolean
      timeSpentMs: number
      userAnswer: unknown
    }[]
  >([])
  const [currentIdx, setCurrentIdx] = useState(0)
  const [showResult, setShowResult] = useState(false)
  const startTime = useRef(Date.now())

  const slides = capsule.slides ?? []
  const slide = slides[currentIdx]
  const isLast = currentIdx === slides.length - 1

  useEffect(() => {
    setAnswered(false)
    setSelected([])
    setBlankAnswers({})
    setMatchAnswers({})
    startTime.current = Date.now()
  }, [currentIdx])

  const options =
    slide?.type === 'true_false'
      ? [
          {
            id: 'true',
            label: 'True',
            isCorrect: slide.answer === 'true' || slide.answer === true,
          },
          {
            id: 'false',
            label: 'False',
            isCorrect: slide.answer === 'false' || slide.answer === false,
          },
        ]
      : (slide?.options ?? []).map(o => ({...o, id: o.id ?? o.label}))

  const isMC = slide?.type === 'multiple_choice'

  const handleToggle = (label: string) => {
    if (answered) return
    if (isMC) {
      setSelected(prev =>
        prev.includes(label) ? prev.filter(x => x !== label) : [...prev, label],
      )
    } else {
      setSelected([label])
    }
  }

  const handleSubmit = () => {
    if (!slide) return
    setAnswered(true)

    let isCorrect = false
    let userAnswer: unknown = selected

    const type = slide.type
    if (
      type === 'single_choice' ||
      type === 'true_false' ||
      type === 'image_choice'
    ) {
      isCorrect = options.some(o => o.isCorrect && selected.includes(o.label))
    } else if (type === 'multiple_choice') {
      const correct = new Set(
        options.filter(o => o.isCorrect).map(o => o.label),
      )
      const sel = new Set(selected)
      isCorrect =
        correct.size === sel.size && [...correct].every(x => sel.has(x))
    } else if (type === 'fill_blank') {
      isCorrect = (slide.blanks ?? []).every(b => {
        const ua = (blankAnswers[b.id] ?? '').trim().toLowerCase()
        return (
          ua === b.answer.trim().toLowerCase() ||
          (b.alternatives ?? []).some(a => a.trim().toLowerCase() === ua)
        )
      })
      userAnswer = blankAnswers
    } else if (type === 'matching') {
      isCorrect = (slide.pairs ?? []).every(
        p => matchAnswers[p.id]?.trim() === p.right.trim(),
      )
      userAnswer = matchAnswers
    } else if (type === 'ordering') {
      const correct = slide.correctOrder ?? []
      isCorrect =
        selected.length === correct.length &&
        selected.every((id, i) => id === correct[i])
    }

    if (settings.enableHaptics) {
      if (isCorrect) {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success)
      } else {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error)
      }
    }

    const result = {
      pageId: slide.id,
      isCorrect,
      timeSpentMs: Date.now() - startTime.current,
      userAnswer,
    }
    const newResults = [...results, result]
    setResults(newResults)
    onPageComplete(result)

    if (settings.instantFeedback) {
      setTimeout(() => advance(newResults), 900)
    }
  }

  const advance = (
    _r: {
      pageId: string
      isCorrect: boolean
      timeSpentMs: number
      userAnswer: unknown
    }[],
  ) => {
    if (isLast) {
      setShowResult(true)
    } else {
      setCurrentIdx(i => i + 1)
    }
  }

  const handleNext = () => advance(results)

  if (showResult) {
    const correct = results.filter(r => r.isCorrect).length
    const pct =
      results.length > 0 ? Math.round((correct / results.length) * 100) : 100
    return (
      <LinearGradient
        colors={gradientColors}
        style={[styles.root, {paddingTop: insets.top}]}>
        <View style={styles.resultContainer}>
          <View style={styles.glassCard}>
            <Text style={styles.resultEmoji}>🏆</Text>
            <Text style={styles.resultPct}>{pct}%</Text>
            <Text style={styles.resultLabel}>Score</Text>
            <Text style={styles.resultSub}>
              {correct} / {results.length} correct
            </Text>
          </View>
          <Pressable
            accessibilityRole="button"
            style={styles.doneBtn}
            onPress={() => onCapsuleComplete(results)}>
            <Text style={styles.doneTxt}>Done</Text>
          </Pressable>
        </View>
      </LinearGradient>
    )
  }

  if (!slide) return null

  // Content slide
  if (slide.type === 'content') {
    return (
      <LinearGradient
        colors={gradientColors}
        style={[styles.root, {paddingTop: insets.top}]}>
        <ScrollView
          contentContainerStyle={[
            styles.scroll,
            {paddingBottom: insets.bottom + 80},
          ]}>
          <View style={styles.glassCard}>
            {slide.contentTitle ? (
              <Text style={styles.question}>{slide.contentTitle}</Text>
            ) : null}
            {slide.contentText ? (
              <Text style={styles.contentBody}>{slide.contentText}</Text>
            ) : null}
          </View>
        </ScrollView>
        <View style={[styles.footer, {paddingBottom: insets.bottom + 12}]}>
          <Pressable
            accessibilityRole="button"
            style={styles.nextBtn}
            onPress={handleNext}>
            <Text style={styles.nextTxt}>Continue →</Text>
          </Pressable>
        </View>
      </LinearGradient>
    )
  }

  const progress = slides.length > 0 ? (currentIdx + 1) / slides.length : 0
  const needsSubmit =
    slide.type === 'multiple_choice' ||
    slide.type === 'fill_blank' ||
    slide.type === 'matching' ||
    slide.type === 'ordering'
  const canSubmit =
    needsSubmit &&
    (slide.type === 'fill_blank'
      ? (slide.blanks ?? []).every(
          b => (blankAnswers[b.id] ?? '').trim().length > 0,
        )
      : slide.type === 'matching'
        ? (slide.pairs ?? []).every(
            p => (matchAnswers[p.id] ?? '').trim().length > 0,
          )
        : selected.length > 0)

  return (
    <LinearGradient
      colors={gradientColors}
      style={[styles.root, {paddingTop: insets.top}]}>
      {settings.showProgressBar && (
        <View style={styles.progressBar}>
          <View style={[styles.progressFill, {width: `${progress * 100}%`}]} />
        </View>
      )}

      <ScrollView
        contentContainerStyle={[
          styles.scroll,
          {paddingBottom: insets.bottom + 80},
        ]}
        showsVerticalScrollIndicator={false}>
        <View style={styles.counter}>
          <Text style={styles.counterTxt}>
            {currentIdx + 1} / {slides.length}
          </Text>
        </View>

        <View style={styles.glassCard}>
          <Text style={styles.question}>{slide.question}</Text>
        </View>

        {/* Choice options */}
        {(slide.type === 'single_choice' ||
          slide.type === 'multiple_choice' ||
          slide.type === 'true_false' ||
          slide.type === 'image_choice') && (
          <View style={styles.options}>
            {options.map((opt, idx) => {
              const isSel = selected.includes(opt.label)
              const isCorrectOpt = opt.isCorrect
              const isWrong = answered && isSel && !isCorrectOpt
              const isRight = answered && isCorrectOpt
              return (
                <Pressable
                  accessibilityRole="button"
                  key={opt.id ?? idx}
                  onPress={() => handleToggle(opt.label)}
                  disabled={answered}
                  style={[
                    styles.optBtn,
                    isSel && !answered && styles.optSelected,
                    isRight && styles.optCorrect,
                    isWrong && styles.optWrong,
                  ]}>
                  <View
                    style={[
                      styles.optBadge,
                      isSel &&
                        !answered && {backgroundColor: 'rgba(168,85,247,0.4)'},
                      isRight && {backgroundColor: 'rgba(34,197,94,0.4)'},
                      isWrong && {backgroundColor: 'rgba(239,68,68,0.4)'},
                    ]}>
                    <Text style={styles.optBadgeTxt}>
                      {String.fromCharCode(65 + idx)}
                    </Text>
                  </View>
                  <Text style={styles.optLabel}>{opt.label}</Text>
                  {answered && isCorrectOpt && (
                    <Text style={{color: '#4ade80', fontWeight: '700'}}>✓</Text>
                  )}
                </Pressable>
              )
            })}
          </View>
        )}

        {/* Fill in the blank */}
        {slide.type === 'fill_blank' && (
          <View style={styles.options}>
            {(slide.blanks ?? []).map((blank, idx) => {
              const ua = blankAnswers[blank.id] ?? ''
              const isCorrect =
                answered &&
                (ua.trim().toLowerCase() ===
                  blank.answer.trim().toLowerCase() ||
                  (blank.alternatives ?? []).some(
                    a => a.trim().toLowerCase() === ua.trim().toLowerCase(),
                  ))
              const isWrong = answered && !isCorrect
              return (
                <View key={blank.id} style={styles.blankRow}>
                  {blank.before ? (
                    <Text style={styles.blankContext}>{blank.before}</Text>
                  ) : null}
                  <TextInput
                    accessibilityLabel="Text input field"
                    accessibilityHint="Enter your answer for this blank"
                    style={[
                      styles.blankInput,
                      isCorrect && {borderColor: '#4ade80'},
                      isWrong && {borderColor: '#f87171'},
                    ]}
                    value={ua}
                    onChangeText={v =>
                      setBlankAnswers(prev => ({...prev, [blank.id]: v}))
                    }
                    placeholder={`Blank ${idx + 1}`}
                    placeholderTextColor="rgba(255,255,255,0.3)"
                    editable={!answered}
                  />
                  {blank.after ? (
                    <Text style={styles.blankContext}>{blank.after}</Text>
                  ) : null}
                  {answered && isWrong && (
                    <Text style={styles.correctHint}>→ {blank.answer}</Text>
                  )}
                </View>
              )
            })}
          </View>
        )}

        {/* Matching */}
        {slide.type === 'matching' && (
          <View style={styles.options}>
            {(slide.pairs ?? []).map(pair => {
              const ua = matchAnswers[pair.id] ?? ''
              const isCorrect = answered && ua.trim() === pair.right.trim()
              const isWrong = answered && !isCorrect
              return (
                <View key={pair.id} style={styles.matchRow}>
                  <View style={styles.matchLeft}>
                    <Text style={styles.matchLeftTxt}>{pair.left}</Text>
                  </View>
                  <Text style={styles.pairArrow}>↔</Text>
                  <TextInput
                    accessibilityLabel="Text input field"
                    accessibilityHint="Enter the matching value"
                    style={[
                      styles.matchInput,
                      isCorrect && {borderColor: '#4ade80'},
                      isWrong && {borderColor: '#f87171'},
                    ]}
                    value={ua}
                    onChangeText={v =>
                      setMatchAnswers(prev => ({...prev, [pair.id]: v}))
                    }
                    placeholder="Match…"
                    placeholderTextColor="rgba(255,255,255,0.3)"
                    editable={!answered}
                  />
                </View>
              )
            })}
          </View>
        )}

        {/* Ordering */}
        {slide.type === 'ordering' && (
          <View style={styles.options}>
            <Text style={styles.orderHint}>
              Tap items in the correct order:
            </Text>
            {(slide.orderItems ?? []).map(item => {
              const pos = selected.indexOf(item.id)
              const isSel = pos !== -1
              const correctIdx = (slide.correctOrder ?? []).indexOf(item.id)
              const isRight = answered && isSel && pos === correctIdx
              const isWrong = answered && isSel && pos !== correctIdx
              return (
                <Pressable
                  accessibilityRole="button"
                  key={item.id}
                  disabled={answered || isSel}
                  onPress={() => setSelected(prev => [...prev, item.id])}
                  style={[
                    styles.optBtn,
                    isSel && !answered && styles.optSelected,
                    isRight && styles.optCorrect,
                    isWrong && styles.optWrong,
                  ]}>
                  {isSel && (
                    <View
                      style={[
                        styles.optBadge,
                        {backgroundColor: 'rgba(168,85,247,0.4)'},
                      ]}>
                      <Text style={styles.optBadgeTxt}>{pos + 1}</Text>
                    </View>
                  )}
                  <Text style={styles.optLabel}>{item.label}</Text>
                </Pressable>
              )
            })}
            {selected.length > 0 && !answered && (
              <Pressable
                accessibilityRole="button"
                onPress={() => setSelected([])}>
                <Text style={styles.resetTxt}>Reset order</Text>
              </Pressable>
            )}
          </View>
        )}

        {answered && slide.explanation ? (
          <View style={styles.explanation}>
            <Text style={styles.explanationTxt}>💡 {slide.explanation}</Text>
          </View>
        ) : null}
      </ScrollView>

      <View style={[styles.footer, {paddingBottom: insets.bottom + 12}]}>
        {answered ? (
          <Pressable
            accessibilityRole="button"
            style={styles.nextBtn}
            onPress={handleNext}>
            <Text style={styles.nextTxt}>
              {isLast ? 'See results' : 'Continue →'}
            </Text>
          </Pressable>
        ) : needsSubmit ? (
          <Pressable
            accessibilityRole="button"
            style={[styles.nextBtn, {opacity: canSubmit ? 1 : 0.4}]}
            onPress={handleSubmit}
            disabled={!canSubmit}>
            <Text style={styles.nextTxt}>Submit</Text>
          </Pressable>
        ) : (
          <Pressable
            accessibilityRole="button"
            style={[styles.nextBtn, {opacity: selected.length > 0 ? 1 : 0.4}]}
            onPress={handleSubmit}
            disabled={selected.length === 0}>
            <Text style={styles.nextTxt}>Submit</Text>
          </Pressable>
        )}
      </View>
    </LinearGradient>
  )
}

const GLASS = 'rgba(255,255,255,0.1)'
const GLASS_BORDER = 'rgba(255,255,255,0.2)'

const styles = StyleSheet.create({
  root: {flex: 1},
  progressBar: {height: 3, backgroundColor: 'rgba(255,255,255,0.15)'},
  progressFill: {
    height: '100%',
    backgroundColor: 'rgba(255,255,255,0.6)',
    borderRadius: 2,
  },
  scroll: {paddingHorizontal: 20, paddingTop: 16, gap: 16},
  counter: {alignSelf: 'center'},
  counterTxt: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1,
  },
  glassCard: {
    backgroundColor: GLASS,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: GLASS_BORDER,
    padding: 20,
    gap: 8,
    alignItems: 'center',
  },
  question: {
    fontSize: 19,
    fontWeight: '700',
    color: '#fff',
    textAlign: 'center',
    lineHeight: 28,
  },
  contentBody: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.75)',
    lineHeight: 22,
    textAlign: 'center',
  },
  options: {gap: 10},
  optBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: GLASS,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: GLASS_BORDER,
    padding: 14,
  },
  optSelected: {
    backgroundColor: 'rgba(168,85,247,0.2)',
    borderColor: 'rgba(168,85,247,0.6)',
  },
  optCorrect: {
    backgroundColor: 'rgba(34,197,94,0.15)',
    borderColor: 'rgba(34,197,94,0.5)',
  },
  optWrong: {
    backgroundColor: 'rgba(239,68,68,0.15)',
    borderColor: 'rgba(239,68,68,0.5)',
  },
  optBadge: {
    width: 28,
    height: 28,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  optBadgeTxt: {color: '#fff', fontSize: 12, fontWeight: '700'},
  optLabel: {flex: 1, color: '#fff', fontSize: 15, fontWeight: '500'},
  explanation: {
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderRadius: 14,
    padding: 14,
    borderWidth: 1,
    borderColor: GLASS_BORDER,
  },
  explanationTxt: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: 13,
    lineHeight: 19,
  },
  blankRow: {gap: 4},
  blankContext: {color: 'rgba(255,255,255,0.6)', fontSize: 13},
  blankInput: {
    color: '#fff',
    fontSize: 14,
    backgroundColor: GLASS,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: GLASS_BORDER,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  correctHint: {color: '#4ade80', fontSize: 12, fontWeight: '600'},
  matchRow: {flexDirection: 'row', alignItems: 'center', gap: 8},
  matchLeft: {
    flex: 1,
    backgroundColor: GLASS,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: GLASS_BORDER,
    paddingHorizontal: 10,
    paddingVertical: 10,
  },
  matchLeftTxt: {color: '#fff', fontSize: 13, fontWeight: '600'},
  pairArrow: {color: 'rgba(255,255,255,0.4)', fontSize: 14},
  matchInput: {
    flex: 1,
    color: '#fff',
    fontSize: 13,
    backgroundColor: GLASS,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: GLASS_BORDER,
    paddingHorizontal: 10,
    paddingVertical: 10,
  },
  orderHint: {color: 'rgba(255,255,255,0.4)', fontSize: 12, marginBottom: 4},
  resetTxt: {
    color: 'rgba(168,85,247,0.8)',
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'right',
  },
  footer: {paddingHorizontal: 20, paddingTop: 10},
  nextBtn: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 24,
    paddingVertical: 16,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: GLASS_BORDER,
  },
  nextTxt: {color: '#fff', fontSize: 15, fontWeight: '800'},
  resultContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    gap: 24,
  },
  resultEmoji: {fontSize: 64, textAlign: 'center'},
  resultPct: {
    fontSize: 56,
    fontWeight: '900',
    color: '#fff',
    textAlign: 'center',
  },
  resultLabel: {
    fontSize: 13,
    fontWeight: '700',
    color: 'rgba(255,255,255,0.6)',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  resultSub: {
    fontSize: 15,
    color: 'rgba(255,255,255,0.8)',
    textAlign: 'center',
    lineHeight: 22,
  },
  doneBtn: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 24,
    paddingVertical: 16,
    paddingHorizontal: 48,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: GLASS_BORDER,
  },
  doneTxt: {color: '#fff', fontSize: 15, fontWeight: '800'},
})
